import { useEffect, useMemo, type ReactElement } from "react";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { RouterProvider } from "react-router-dom";
import { buildTheme } from "./lib/theme";
import { AccountQueryProvider } from "./lib/queryClient";
import { router } from "./routes";
import { useThemeStore } from "./core/stores/useThemeStore";
import { useAuthStore } from "./core/stores/useAuthStore";
import { ToastHost } from "./core/components/ToastHost";
import { ShortcutsHelpDialog } from "./core/components/ShortcutsHelpDialog";
import { ErrorBoundary } from "./core/components/ErrorBoundary";
import { NEW_UI } from "./features/orders/localPreview";

export function App(): ReactElement {
  const mode = useThemeStore((s) => s.mode);
  const hydrate = useAuthStore((s) => s.hydrate);
  const accountId = useAuthStore((s) => s.user?.id ?? "signed-out");
  const theme = useMemo(() => buildTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    const keepNumberOnScroll = (event: WheelEvent): void => {
      const input = event.target;
      if (
        input instanceof HTMLInputElement &&
        input.type === "number" &&
        input === document.activeElement
      ) {
        // Blur before the browser steps the number; leave native page/dialog scrolling alone.
        input.blur();
      }
    };
    // Capture also covers portalled dialogs and the remaining MUI fields.
    document.addEventListener("wheel", keepNumberOnScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener("wheel", keepNumberOnScroll, true);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {import.meta.env.MODE === "sandbox" ? (
        <Box
          role="status"
          sx={{
            position: "fixed",
            right: 8,
            top: 4,
            zIndex: 2000,
            px: 1,
            py: 0.25,
            bgcolor: "warning.light",
            color: "#111",
            borderRadius: 1,
            fontSize: 11,
            fontWeight: 700,
            pointerEvents: "none",
          }}
        >
          LOCAL TEST
        </Box>
      ) : null}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AccountQueryProvider key={accountId}>
          <ErrorBoundary>
            <RouterProvider router={router} />
            <ToastHost />
            {!NEW_UI ? <ShortcutsHelpDialog /> : null}
          </ErrorBoundary>
        </AccountQueryProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
